/**
 * Personalization Utilities
 *
 * Helper functions for matching worksheets to teacher preferences
 * based on grade levels and subjects from onboarding data.
 */

import type { Worksheet } from '@/services/worksheet.service';

// =============================================================================
// Types
// =============================================================================

export interface TeacherPreferences {
  gradeLevels: string[];
  subjects: string[];
}

export interface PersonalizedWorksheet extends Worksheet {
  matchesGrade: boolean;
  matchesSubject: boolean;
  isRecommended: boolean;
}

// =============================================================================
// Grade Normalization
// =============================================================================

/**
 * Grade mappings from profile format to worksheet format.
 * Profile uses: "kindergarten", "grade-1", "grade-2", etc.
 * Worksheet uses: "K", "1", "2", etc.
 */
const GRADE_MAP: Record<string, string> = {
  'kindergarten': 'K',
  'grade-k': 'K',
  'grade-1': '1',
  'grade-2': '2',
  'grade-3': '3',
  'grade-4': '4',
  'grade-5': '5',
  'grade-6': '6',
};

/**
 * Normalize a grade level to worksheet format.
 * Handles both profile format ("grade-1") and worksheet format ("1").
 */
export function normalizeGrade(grade: string): string {
  const lower = grade.toLowerCase().trim();
  return GRADE_MAP[lower] ?? grade;
}

/**
 * Check if a worksheet grade matches any of the teacher's grade levels.
 */
export function matchesGradeLevel(
  worksheetGrade: string,
  teacherGradeLevels: string[]
): boolean {
  if (!teacherGradeLevels.length) return false;

  const normalizedWorksheetGrade = normalizeGrade(worksheetGrade);
  return teacherGradeLevels.some(
    (teacherGrade) => normalizeGrade(teacherGrade) === normalizedWorksheetGrade
  );
}

// =============================================================================
// Subject Normalization
// =============================================================================

/**
 * Subject mappings for normalization.
 * Profile uses: "mathematics", "social-studies", "english-language-arts", "science"
 * Worksheet uses: "mathematics", "social_studies", "english_language_arts", "science"
 */
const SUBJECT_MAP: Record<string, string> = {
  'social-studies': 'social_studies',
  'english-language-arts': 'english_language_arts',
  // Already matching: mathematics, science
};

/**
 * Normalize a subject to worksheet format.
 */
export function normalizeSubject(subject: string): string {
  const lower = subject.toLowerCase().trim();
  return SUBJECT_MAP[lower] ?? lower;
}

/**
 * Check if a worksheet subject matches any of the teacher's subjects.
 */
export function matchesSubject(
  worksheetSubject: string,
  teacherSubjects: string[]
): boolean {
  if (!teacherSubjects.length) return false;

  const normalizedWorksheetSubject = normalizeSubject(worksheetSubject);
  return teacherSubjects.some(
    (teacherSubject) => normalizeSubject(teacherSubject) === normalizedWorksheetSubject
  );
}

// =============================================================================
// Worksheet Personalization
// =============================================================================

/**
 * Enhance worksheets with personalization flags based on teacher preferences.
 */
export function personalizeWorksheets(
  worksheets: Worksheet[],
  preferences: TeacherPreferences
): PersonalizedWorksheet[] {
  return worksheets.map((worksheet) => {
    const matchesGradeFlag = matchesGradeLevel(worksheet.grade, preferences.gradeLevels);
    const matchesSubjectFlag = matchesSubject(worksheet.subject, preferences.subjects);

    return {
      ...worksheet,
      matchesGrade: matchesGradeFlag,
      matchesSubject: matchesSubjectFlag,
      // Recommended only if matches BOTH grade AND subject
      isRecommended: matchesGradeFlag && matchesSubjectFlag,
    };
  });
}

/**
 * Sort worksheets to prioritize teacher's preferences.
 * Order:
 * 1. Matches both grade AND subject
 * 2. Matches grade only
 * 3. Matches subject only
 * 4. No match
 */
export function sortByRelevance(worksheets: PersonalizedWorksheet[]): PersonalizedWorksheet[] {
  return [...worksheets].sort((a, b) => {
    // Score: 2 for matching both, 1 for matching one, 0 for none
    const scoreA = (a.matchesGrade ? 1 : 0) + (a.matchesSubject ? 1 : 0);
    const scoreB = (b.matchesGrade ? 1 : 0) + (b.matchesSubject ? 1 : 0);

    // Higher score first
    if (scoreB !== scoreA) return scoreB - scoreA;

    // Within same score, prioritize grade match over subject match
    if (a.matchesGrade !== b.matchesGrade) return a.matchesGrade ? -1 : 1;

    // Finally, alphabetical by title
    return a.title.localeCompare(b.title);
  });
}

/**
 * Get worksheets that match teacher's preferences (recommended).
 */
export function getRecommendedWorksheets(
  worksheets: Worksheet[],
  preferences: TeacherPreferences,
  limit?: number
): PersonalizedWorksheet[] {
  const personalized = personalizeWorksheets(worksheets, preferences);
  const recommended = personalized.filter((w) => w.isRecommended);
  const sorted = sortByRelevance(recommended);
  return limit ? sorted.slice(0, limit) : sorted;
}

// =============================================================================
// Display Helpers
// =============================================================================

/**
 * Get display labels for grade levels.
 */
export function getGradeDisplayLabel(grade: string): string {
  const normalized = normalizeGrade(grade);
  if (normalized === 'K') return 'Kindergarten';
  return `Grade ${normalized}`;
}

/**
 * Get display labels for subjects.
 */
export function getSubjectDisplayLabel(subject: string): string {
  const labels: Record<string, string> = {
    'mathematics': 'Mathematics',
    'social_studies': 'Social Studies',
    'social-studies': 'Social Studies',
    'english_language_arts': 'English Language Arts',
    'english-language-arts': 'English Language Arts',
    'science': 'Science',
  };
  return labels[subject.toLowerCase()] ?? subject;
}

/**
 * Check if teacher has any preferences set.
 */
export function hasPreferences(preferences: TeacherPreferences): boolean {
  return preferences.gradeLevels.length > 0 || preferences.subjects.length > 0;
}
